import { Injectable, Injector, NgZone } from '@angular/core';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import {
  AngularFirestore,
} from '@angular/fire/compat/firestore';
import { Router } from '@angular/router';
import { OAuthProvider } from 'firebase/auth';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { WebServiceService } from './web/web-service.service';
import { BehaviorSubject } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  userData: any; // Save logged in user data
  msProvider = new OAuthProvider('microsoft.com');
  groups$ = new BehaviorSubject<string[]>(JSON.parse(localStorage.getItem('groups') || '[]'));

  private webService!: WebServiceService;

  constructor(
    public afs: AngularFirestore, // Inject Firestore service
    public afAuth: AngularFireAuth, // Inject Firebase auth service
    public router: Router,
    public ngZone: NgZone, // NgZone service to remove outside scope warning
    private http: HttpClient,
    private injector: Injector
  ) {
    /* Saving user data in localstorage when
    logged in and setting up null when logged out */
    this.afAuth.authState.subscribe(async (user) => {
      if (user) {
        this.userData = user;

        localStorage.setItem('user', JSON.stringify(this.userData));
        JSON.parse(localStorage.getItem('user')!);

        // Anonymous users (Eltern-Flow) get their groups set by verifyParent — skip premature fetch
        if (!user.isAnonymous) {
          this.webService = this.injector.get(WebServiceService);
          setTimeout(() => {
            this.webService.getUserGroups().subscribe({
              next: (groups: any) => {
                localStorage.setItem('groups', JSON.stringify(groups.data));
                JSON.parse(localStorage.getItem('groups')!);
                this.groups$.next(groups.data);
              },
              error: () => {
                // User document not yet created (e.g. during registration) — keep groups empty
                localStorage.removeItem('groups');
                this.groups$.next([]);
              }
            });
          }, 100);
        }
      } else {
        localStorage.setItem('user', 'null');
        JSON.parse(localStorage.getItem('user')!);
        localStorage.removeItem('groups');
        this.groups$.next([]);
      }
    });
    this.msProvider.setCustomParameters({
      // prompt: 'consent',
      tenant: '8761df57-9fa9-43b0-97bd-ff080b598cd0',
    });
    // this.msProvider.addScope('calendars.read');
  }
  // Sign in with email/password
  SignIn(email: string, password: string, redirectURL?: string) {
    return this.afAuth
      .signInWithEmailAndPassword(email, password)
      .then((result) => {
        this.afAuth.authState.subscribe((user) => {
          if (user) {
            if (redirectURL) {
              this.router.navigate(redirectURL.split('/'));
            } else {
              this.router.navigate(['participant']);
            }
          }
        });
      })
      .catch((error) => {
        window.alert(error.message);
      });
  }
  signInAnonym(participantId: string, parentId: string, password: string) {
    return this.afAuth.signInAnonymously().then(async (result) => {
      const { user } = result;

      if (user) {
        this.userData = user;
        this.userData.idToken = await user.getIdToken(true);
        localStorage.setItem('user', JSON.stringify(this.userData));
      } else {
        localStorage.setItem('user', 'null');
        JSON.parse(localStorage.getItem('user')!);
      }
    });
  }

  SignUp(email: string, password: string) {
    return this.afAuth
      .createUserWithEmailAndPassword(email, password)
      .then(async (result) => {
        // this.SendVerificationMail();

        const { user } = result;
        if (user) {
          this.userData = user;
          localStorage.setItem('user', JSON.stringify(this.userData));
          JSON.parse(localStorage.getItem('user')!);
        } else {
          localStorage.setItem('user', 'null');
          JSON.parse(localStorage.getItem('user')!);
        }
        return result.user;
      });
  }

  SendVerificationMail() {
    return this.afAuth.currentUser
      .then((u: any) => u.sendEmailVerification())
      .then(() => {
        this.router.navigate(['verify-email-address']);
      });
  }

  ForgotPassword(passwordResetEmail: string) {
    return this.afAuth
      .sendPasswordResetEmail(passwordResetEmail)
      .then(() => {
        window.alert('Password reset email sent, check your inbox.');
      })
      .catch((error) => {
        window.alert(error);
      });
  }
  // Returns true when user is looged in and email is verified
  get isLoggedIn(): boolean {
    const user = JSON.parse(localStorage.getItem('user')!);
    return user !== null ? true : false;
  }

  microsoftAuth() {
    this.afAuth.signInWithPopup(this.msProvider).then(async (result) => {
      // User is signed in.
      // IdP data available in result.additionalUserInfo.profile.

      // Get the OAuth access token and ID Token
      const credential = OAuthProvider.credentialFromResult(result as any);
      // const accessToken = credential?.accessToken;
      // const idToken = credential?.idToken;
      this.http
        .post(
          environment.functionsURL + 'apiV2/admin/loginMicrosoft',
          {},
          {
            headers: new HttpHeaders({
              Authorization: 'Bearer ' + (await result.user?.getIdToken()),
            }),
          }
        )
        .subscribe();

      this.router.navigate(['admin']);
      this.router.navigate(['admin']);
    });
  }
  // Auth logic to run auth providers
  AuthLogin(provider: any) {
    return this.afAuth
      .signInWithRedirect(provider)
      .then((result) => {
        this.router.navigate(['participant']);
      })
      .catch((error) => {
        window.alert(error);
      });
  }
  // Sign out
  SignOut(dontRedirect?: boolean) {
    return this.afAuth.signOut().then(() => {
      localStorage.removeItem('user');
      localStorage.removeItem('groups');
      if (!dontRedirect) {
        this.router.navigate(['sign-in']);
      }
    });
  }

  getUserId() {
    return JSON.parse(localStorage.getItem('user')!).uid;
  }

  getUserEmail(): string {
    return this.userData.email;
  }

  changePassword(currentPassword: string, newPassword: string): Promise<void> {
    return this.afAuth
      .signInWithEmailAndPassword(this.getUserEmail(), currentPassword)
      .then((userCredential) => {
        if (!userCredential || !userCredential.user) {
          return;
        }
        return userCredential.user.updatePassword(newPassword);
      });
  }
}
